/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from 'react';

import { Column } from './components/column';
import { CopyrightSection } from './components/copyright-section';
import { IconDatocmsRendered } from './components/icon-datocms-rendered';
import { RebrandNumaLogo } from './components/logo';
import { SiteLink } from './components/types';
import './styles.css';
import styles from './styles.module.css';

type FooterV2Props = {
  className?: string;
  floatButton?: boolean;

  websiteUrl: string;
  variant?: 'large' | 'small';

  content: {
    siteLink: SiteLink;
    i18n: {
      copyrightText: string;
    };
    homePage: {
      rebrandedHeroTitleLine1: string;
      rebrandedHeroTitleLine2: string;
    };
  };

  onFAQClick: VoidFunction;
  onShowConsentModal?: VoidFunction;
  locale: string;
};

export function Footer({
  floatButton,
  variant = 'small',
  websiteUrl,
  content,
  className = '',
  onFAQClick,
  onShowConsentModal,
  locale,
}: FooterV2Props) {
  const { i18n, homePage, siteLink } = content;
  const [userDevice, setUserDevice] = useState<'apple' | 'android' | null>(null);

  useEffect(() => {
    if (!window) return;

    const { userAgent } = window.navigator;
    const isAppleDevice = /iPhone|iPod|iPad/.test(userAgent);
    const isAndroidDevice = /Android/.test(userAgent);

    if (isAppleDevice) {
      setUserDevice('apple');
    } else if (isAndroidDevice) {
      setUserDevice('android');
    }
  }, []);

  const { discover, aboutUs, contactAndHelp, staySocial, downloadNuma, payWith } = siteLink;

  const { appleStoreIcon, googlePlayStoreIcon } = downloadNuma;

  return (
    <div className={`${styles.container} ${floatButton && styles.floatButton} ${className}`}>
      <div className={styles.contentWidthLimiter}>
        <div className={styles.contentPaddingWrapper}>
          <div className={styles.wrapper}>
            {/* This Download Column only visible on mobile */}
            <div className={styles.downloadColumn}>
              <div className={styles.titleColumn}>{downloadNuma.title}</div>
              <div className={styles.downloadRow}>
                {userDevice === 'apple' && <IconDatocmsRendered {...appleStoreIcon} />}

                {userDevice === 'android' && <IconDatocmsRendered {...googlePlayStoreIcon} />}
                {userDevice === null && (
                  <>
                    <IconDatocmsRendered {...appleStoreIcon} />
                    <IconDatocmsRendered {...googlePlayStoreIcon} />
                  </>
                )}
              </div>
            </div>

            <Column
              websiteUrl={websiteUrl}
              title={siteLink.contactTitle}
              items={contactAndHelp}
              locale={locale}
              onFAQClick={onFAQClick}
            />

            <Column websiteUrl={websiteUrl} title={siteLink.discoverTitle} items={discover} locale={locale} />

            <Column websiteUrl={websiteUrl} title="Numa" items={aboutUs} locale={locale} />

            <div className={styles.socialColumn}>
              <div className={styles.socialCollection}>
                <div className={styles.titleColumn}>{staySocial.title}</div>
                <div className={styles.socialRow}>
                  {staySocial.images.map((staySocialItem) => {
                    return (
                      <IconDatocmsRendered
                        key={staySocialItem.id}
                        className={styles.socialItem ?? ''}
                        {...staySocialItem}
                      />
                    );
                  })}
                </div>
              </div>

              {/* This Download Column only visible on desktop */}
              <div className={styles.socialCollection}>
                <div className={styles.titleColumn}>{downloadNuma.title}</div>
                <div className={styles.downloadRow}>
                  <IconDatocmsRendered {...appleStoreIcon} />
                  <IconDatocmsRendered {...googlePlayStoreIcon} />
                </div>
              </div>

              <div className={styles.socialCollection}>
                <div className={styles.titleColumn}>{payWith.title}</div>
                <div className={styles.downloadRow}>
                  {payWith.images.map((payItem) => {
                    return <IconDatocmsRendered key={payItem.id} {...payItem} className={styles.payItem} />;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.contentWidthLimiter}>
        <div className={styles.contentPaddingWrapper}>
          <div className={`${styles.logoContainer} ${variant === 'large' && styles.logoContainerLarge}`}>
            <div className={`${styles.numaLogo} ${variant === 'large' && styles.numaLogoLarge}`}>
              <RebrandNumaLogo />
            </div>
            <div className={`${styles.tagLine} ${variant === 'large' && styles.tagLineLarge}`}>
              <span>{homePage.rebrandedHeroTitleLine1}</span>
              <span>{homePage.rebrandedHeroTitleLine2}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.contentWidthLimiter}>
        <div className={styles.contentPaddingWrapper}>
          <CopyrightSection
            websiteUrl={websiteUrl}
            copyright={siteLink.copyright}
            dataPreference={siteLink.dataPreference}
            showConsentModal={onShowConsentModal ?? (() => {})}
            copyrightText={`© ${i18n.copyrightText}`}
            locale={locale}
          />
        </div>
      </div>
    </div>
  );
}
